import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

// App screenshots from public directory
const appScreenshots = [
  '/img/dashboard.jpeg',
  '/img/add_meal_1.jpeg',
  '/img/add_meal_2.jpeg',
  '/img/add_meal_3.jpeg',
  '/img/meal_details.jpeg',
  '/img/edit_goals.jpeg',
  '/img/log_weight.jpeg',
  '/img/log_body_fat.jpeg',
  '/img/settings.jpeg',
];

function App() {
  const [currentScreenshot, setCurrentScreenshot] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentScreenshot((prev) => (prev + 1) % appScreenshots.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: "📸",
      iconStyle: "warm",
      title: "AI Meal Analysis",
      description: "Snap a photo of any meal and our AI instantly identifies ingredients and calculates nutritional content. No manual entry required."
    },
    {
      icon: "📊",
      iconStyle: "mint",
      title: "Body Composition",
      description: "Track weight and body fat percentage with intuitive logging. Visualize your progress with beautiful, insightful charts."
    },
    {
      icon: "🎯",
      iconStyle: "blue",
      title: "Personal Goals",
      description: "Set customized health targets and stay motivated with progress tracking tailored to your unique wellness journey."
    }
  ];

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="App">
      {/* Ambient background glow */}
      <div className="ambient-glow" />

      {/* Navigation */}
      <nav className="navbar">
        <div className="container">
          <div className="nav-content">
            <button className="logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img src="/img/logo.png" alt="Logo" className="logo-image" />
              <h2>KenkoSnap</h2>
            </button>
            <div className="nav-links">
              <button onClick={() => scrollToSection('features')} className="nav-link">Features</button>
              <button onClick={() => scrollToSection('screenshots')} className="nav-link">Screenshots</button>
              <a href="https://apps.apple.com/us/app/kenkosnap/id6747584961" target="_blank" rel="noopener noreferrer" className="cta-button">
                Download
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <span className="hero-badge-dot" />
                AI-Powered Health Tracking
              </div>
              <h1 className="hero-title">
                Your Smart<br />
                <span className="highlight">Health Companion</span>
              </h1>
              <p className="hero-subtitle">
                Take control of your wellness journey with KenkoSnap.
                AI-powered nutrition tracking and body composition monitoring,
                designed for those who refuse to compromise on their health.
              </p>
              <div className="hero-buttons">
                <a href="https://apps.apple.com/us/app/kenkosnap/id6747584961" target="_blank" rel="noopener noreferrer" className="primary-button">
                  Download for iOS
                </a>
                <button onClick={() => scrollToSection('features')} className="secondary-button">
                  Explore Features
                </button>
              </div>
            </div>
            <div className="hero-image">
              <div className="phone-container">
                <div className="phone-glow" />
                <div className="phone-mockup">
                  {/* Side buttons */}
                  <div className="phone-button phone-button-power" />
                  <div className="phone-button phone-button-volume-up" />
                  <div className="phone-button phone-button-volume-down" />
                  <div className="phone-button phone-button-silent" />
                  {/* Inner frame bezel */}
                  <div className="phone-inner-frame">
                    <div className="phone-screen">
                      {/* Dynamic Island */}
                      <div className="phone-dynamic-island">
                        <div className="dynamic-island-camera" />
                      </div>
                      <img
                        src={appScreenshots[currentScreenshot]}
                        alt="KenkoSnap App Screenshot"
                        className="app-screenshot"
                      />
                      {/* Glass reflection overlay */}
                      <div className="phone-screen-reflection" />
                    </div>
                  </div>
                </div>
                {/* Floating stat cards */}
                <div className="floating-elements">
                  <div className="float-card card-1">
                    <div className="float-icon calories">🔥</div>
                    <div className="float-text">
                      <span className="float-label">Calories</span>
                      <span className="float-value">1,847 kcal</span>
                    </div>
                  </div>
                  <div className="float-card card-2">
                    <div className="float-icon weight">⚖️</div>
                    <div className="float-text">
                      <span className="float-label">Weight</span>
                      <span className="float-value">-2.3 kg</span>
                    </div>
                  </div>
                  <div className="float-card card-3">
                    <div className="float-icon goal">✓</div>
                    <div className="float-text">
                      <span className="float-label">Goal</span>
                      <span className="float-value">On Track</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Features</span>
            <h2 className="section-title">Everything You Need</h2>
            <p className="section-subtitle">
              Powerful tools designed to make health tracking effortless and insightful
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className={`feature-icon-wrapper ${feature.iconStyle}`}>
                  {feature.icon}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <div className="section-header">
            <span className="section-label">How It Works</span>
            <h2 className="section-title">Three Simple Steps</h2>
            <p className="section-subtitle">
              Start your wellness journey in under a minute
            </p>
          </div>
          <div className="steps-grid">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Snap a Photo</h3>
              <p>Point your camera at any meal and take a quick photo</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>AI Analysis</h3>
              <p>Our AI instantly identifies ingredients and calculates nutrition</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>Track Progress</h3>
              <p>Monitor your goals and watch your health transform</p>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots Gallery */}
      <section id="screenshots" className="screenshots">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Gallery</span>
            <h2 className="section-title">See It In Action</h2>
            <p className="section-subtitle">
              A beautifully designed interface for your daily health tracking
            </p>
          </div>
          <div className="screenshots-grid">
            {appScreenshots.slice(0, 5).map((screenshot, index) => (
              <div key={index} className="screenshot-item">
                <img src={screenshot} alt={`KenkoSnap Screenshot ${index + 1}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-card">
            <div className="cta-content">
              <h2 className="cta-title">Start Your Journey Today</h2>
              <p className="cta-subtitle">
                Whether you're aiming to lose fat, build muscle, or maintain a healthy lifestyle —
                KenkoSnap makes it simple, smart, and sustainable.
              </p>
              <a href="https://apps.apple.com/us/app/kenkosnap/id6747584961" target="_blank" rel="noopener noreferrer" className="primary-button large">
                Download KenkoSnap
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>KenkoSnap</h3>
              <p>Your Smart Health Companion</p>
            </div>
            <div className="footer-links">
              <div className="footer-legal">
                <Link to="/terms" className="footer-link">Terms</Link>
                <Link to="/privacy" className="footer-link">Privacy</Link>
                <Link to="/support" className="footer-link">Support</Link>
              </div>
              <p>&copy; 2026 KenkoSnap. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
